/**
 * PrefetchLink — drop-in replacement for react-router `<Link>` that pre-warms
 * the destination route's lazy chunk on hover / focus / touchstart.
 *
 * Usage:
 *   <PrefetchLink to="/market" prefetch={() => import('@/pages/Market')}>
 *     Browse market
 *   </PrefetchLink>
 *
 * The `prefetch` thunk should be the same `() => import(...)` passed to
 * React.lazy() at the route declaration. Webpack/Vite dedupes the dynamic
 * import, so calling it on hover just kicks off the network fetch early —
 * by the time the click navigates, the chunk is parsed and ready.
 *
 * Safe to render server-side: the prefetch is a no-op until the first user
 * pointer event. On touch devices, prefetch fires on touchstart (before the
 * synthetic click) so the tap feels instant.
 */
import { Link, LinkProps } from 'react-router-dom';
import { useCallback, useRef } from 'react';

type Prefetcher = () => Promise<unknown>;

interface PrefetchLinkProps extends LinkProps {
  /** The same `() => import('...')` thunk used by React.lazy for the destination route. */
  prefetch?: Prefetcher;
}

export const PrefetchLink = ({ prefetch, onMouseEnter, onFocus, onTouchStart, onPointerDown, ...rest }: PrefetchLinkProps) => {
  const firedRef = useRef(false);

  const fire = useCallback(() => {
    if (firedRef.current || !prefetch) return;
    firedRef.current = true;
    // Swallow rejections — failed prefetch shouldn't surface as an error.
    prefetch().catch(() => { firedRef.current = false; });
  }, [prefetch]);

  return (
    <Link
      {...rest}
      onMouseEnter={(e) => { fire(); onMouseEnter?.(e); }}
      onFocus={(e) => { fire(); onFocus?.(e); }}
      onTouchStart={(e) => { fire(); onTouchStart?.(e); }}
      // Fire on pointerdown (mouse/pen). Touch already covered by onTouchStart
      // above; this catches mid-clicks + trackpad taps so the chunk warms
      // before the click resolves — shaves an RTT off perceived nav latency.
      onPointerDown={(e) => { if (e.pointerType !== 'touch') fire(); onPointerDown?.(e); }}
    />
  );
};
